import { DEFAULT_API_CONFIG } from './apiConfig';
import { downloadExcelFileToBrowser } from '../helpers';
import queryString from 'query-string';

const { url, getHeaders } = DEFAULT_API_CONFIG;
const baseRoute = `${url}/accounting`;

/**
 * CLIENT INVOICING
 */
export const getClientInvoicesReport = async (endDate = null, period, dueDate) => {
  const url = `${baseRoute}/clients/invoice-report?_endDate=${
    !!endDate ? endDate.toJSON() : ''
  }&_period=${period}&_dueDate=${!!dueDate ? dueDate.toJSON() : ''}`;

  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });
  return response.ok ? await response.json() : Promise.reject(response);
};

export const getExistingClientInvoicesReport = async () => {
  const url = `${baseRoute}/clients/invoice-report`;
  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });
  return response.ok ? await response.json() : Promise.reject(response);
};

export const clientInvoiceReportExists = async () => {
  const url = `${baseRoute}/clients/invoice-report-exists`;
  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });
  return response.ok ? await response.json() : Promise.reject(response);
};

export const getClientInvoicesReportHistory = async (period = null) => {
  const url = `${baseRoute}/clients/invoice-report-history?_period=${!!period ? period : ''}`;
  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });
  return response.ok ? await response.json() : Promise.reject(response);
};

export const getClientInvoicesHistoryPeriods = async (skip = 0, take = 10) => {
  const url = `${baseRoute}/clients/history/periods?_skip=${skip}&_take=${take}`;
  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });
  return response.ok ? await response.json() : Promise.reject(response);
};

export const getClientInvoiceZipForPeriod = async ({invoicePeriod}) => {
  const url = `${baseRoute}/export/InvoiceByAccount?invoicePeriod=${invoicePeriod}`;
  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });
  if (!response.ok) {
    Promise.reject(response);
  }

  let json = await response.json();
  downloadExcelFileToBrowser(json, `Invoice_${invoicePeriod}.zip`);

  return true;
};

export const getClientInvoiceExcel = async invoice => {
  const { invoiceId, clientName, period } = invoice;

  const url = `${baseRoute}/clients/export/${invoiceId}`;
  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    console.log('Invoice export failed...');
    return false;
  }

  let json = await response.json();
  downloadExcelFileToBrowser(json, `${clientName}_${period}.xlsx`);

  return true;
};

export const confirmClientInvoices = async confirmAndExport => {
  const url = `${baseRoute}/clients/report-response/${confirmAndExport}`;
  let headers = getHeaders();

  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify({
      confirmAndExport,
    }),
    headers,
  });

  return response.ok ? response : Promise.reject(response);
};

/**
 * INDIVIDUAL INVOICING (QA AND INSPECTOR)
 */
/**
 * @func getIndividualsPaymentReport
 * @returns { Array } of objects containing financial information for many users of the same type
 * @param {*} endDate
 * @param {*} period
 * @param {*} dueDate
 * @param {*} reportType
 */
export const getIndividualsPaymentReport = async (
  endDate = null,
  period,
  dueDate,
  reportType /* QA or INSPECTOR */,
) => {
  const url = `${baseRoute}/individuals/${reportType}/payment-report?_endDate=${
    !!endDate ? endDate.toJSON() : ''
  }&_period=${period}&_dueDate=${!!dueDate ? dueDate.toJSON() : ''}&_reportType=${reportType}`;

  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });
  return response.ok ? await response.json() : Promise.reject(response);
};

export const individualsPaymentReportExists = async reportType => {
  const url = `${baseRoute}/individuals/${reportType}/payment-report-exists`;
  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });
  return response.ok ? await response.json() : Promise.reject(response);
};

export const confirmIndividualsPaymentReport = async (confirmAndExport, reportType) => {
  const url = `${baseRoute}/individuals/${reportType}/report-response/${confirmAndExport}`;
  let headers = getHeaders();

  const response = await fetch(url, {
    method: 'PUT',
    body: JSON.stringify({
      confirmAndExport,
    }),
    headers,
  });

  return response.ok ? response : Promise.reject(response);
};

export const getIndividualPaymentsReportHistory = async (period = null, reportType) => {
  const url = `${baseRoute}/individuals/${reportType}/payment-report-history?_period=${
    !!period ? period : ''
  }&_reportType=${reportType}`;
  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });
  return response.ok ? await response.json() : Promise.reject(response);
};

export const getSingleIndividualPaymentsReportHistory = async (_periods = [], reportType) => {
  const query = queryString.stringify({
    _periods,
    reportType,
  });
  const url = `${baseRoute}/individuals/${reportType}/single/payment-report-history?${query}`;
  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });
  return response.ok ? await response.json() : Promise.reject(response);
};

export const getSingleIndividualUnpaidOrders = async reportType => {
  const url = `${baseRoute}/individuals/${reportType}/single/unpaid-orders`;
  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });
  return response.ok ? await response.json() : Promise.reject(response);
};

export const getIndividualPaymentsHistoryPeriods = async (reportType, skip = 0, take = 10) => {
  const url = `${baseRoute}/individuals/${reportType}/history/periods?_skip=${skip}&_take=${take}`;
  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });
  return response.ok ? await response.json() : Promise.reject(response);
};

/**
 * THIRD PARTY
 */
export const getThirdPartyCostReport = async ({ startDate = null, endDate = null } = {}) => {
  const url = `${baseRoute}/export/3rdpartycostreport?startDate=${!!startDate ? startDate.toJSON() : ''}&endDate=${
    !!endDate ? endDate.toJSON() : ''
  }`;

  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    console.log('Third Party Cost Report export failed...');
    return false;
  }

  if (response.status === 204) {
    throw new Error('No data available for that date range');
  }

  let json = await response.json();
  downloadExcelFileToBrowser(json, `3rd_party_cost_report.xlsx`);

  return true;
};

/**
 * THIRD PARTY
 */
export const getClosedOrderReport = async ({ startDate = null, endDate = null } = {}) => {
  const url = `${baseRoute}/export/datafile?startDate=${!!startDate ? startDate.toJSON() : ''}&endDate=${
    !!endDate ? endDate.toJSON() : ''
  }`;

  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    console.log('Closed Order Report export failed...');
    return false;
  }

  if (response.status === 204) {
    throw new Error('No data available for that date range');
  }

  let json = await response.json();
  downloadExcelFileToBrowser(json, `closed_order_report.xlsx`);

  return true;
};

export const getClosedOrderReportByInvoicePeriod = async ({ invoicePeriod = null } = {}) => {
  const url = `${baseRoute}/export/datafilebyinvoiceperiod?invoicePeriod=${!!invoicePeriod ? invoicePeriod : ''}`;

  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    console.log('Closed Order Report export failed...');
    return false;
  }

  if (response.status === 204) {
    throw new Error('No data available for that date range');
  }

  let json = await response.json();
  downloadExcelFileToBrowser(json, `closed_order_report.xlsx`);

  return true;
};

export const getNewRevenueReport = async ({ invoicePeriod = null} = {}) => {

  const url = `${baseRoute}/export/NewRevenueReport?invoicePeriod=${!!invoicePeriod ? invoicePeriod : ''}`;

  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    const error = await response.json();
    
    throw new Error(error.Message);
  }

  if (response.status === 204) {
    throw new Error('No data available for the selected parameters');
  }

  let json = await response.json();
  downloadExcelFileToBrowser(json, `new_revenue_report.xlsx`);

  return true;
};

/**
 * BETTERVIEW REPORT
 */
export const getBetterviewReport = async ({ startDate = null, endDate = null } = {}) => {
  const url = `${baseRoute}/export/betterviewreport?startDate=${!!startDate ? startDate.toJSON() : ''}&endDate=${
    !!endDate ? endDate.toJSON() : ''
  }`;

  let headers = getHeaders();
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  if (!response.ok) {
    console.log('Betterview Report export failed...');
    return false;
  }

  if (response.status === 204) {
    throw new Error('No data available for that date range');
  }

  let json = await response.json();
  downloadExcelFileToBrowser(json, `betterview_report.xlsx`);

  return true;
};

/**
 * UNDERWRITER ACTION REPORT
 */
 export const getUnderwriterActionReport = async ({ startDate = null, endDate = null } = {}) => {
    const url = `${baseRoute}/export/underwriteractionreport?startDate=${!!startDate ? startDate.toJSON() : ''}&endDate=${
      !!endDate ? endDate.toJSON() : ''
    }`;
  
    let headers = getHeaders();
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });
  
    if (!response.ok) {
      console.log('UnderwriterAction Report export failed...');
      return false;
    }
  
    if (response.status === 204) {
      throw new Error('No data available for that date range');
    }
  
    let json = await response.json();
    downloadExcelFileToBrowser(json, `underwriter_action_report.xlsx`);
  
    return true;
  };
  